import { useEffect, useMemo, useState } from 'react'
import { isBcaPaymentVendor, isDokuPaymentVendor, isSnapPaymentVendor } from 'Main/paymentVendorTypes'
import { formFromBankRoute } from 'Main/Utils'
import { EMPTY_BANK_ROUTE_FORM } from 'Main/Constant'
import { isBniPaymentVendor } from 'Main/paymentVendorTypes'


function BankRouteModal ({ show, route, vendors, channels, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_BANK_ROUTE_FORM)
  const [activePickerChannel, setactivePickerChannel] = useState(true)
  const isAdd = !route?.id

  useEffect(() => {
    if (show) setForm(formFromBankRoute(route))
  }, [show, route])

  const snapVendors = useMemo(
    () => vendors.filter((vendor) => isSnapPaymentVendor(vendor.enviroment_type)),
    [vendors]
  )
  const selectedVendor = snapVendors.find((vendor) => String(vendor.id) === form.payment_vendors_id)
  const missingVendor = form.payment_vendors_id && !selectedVendor

  const availableChannels = useMemo(() => {
    if (!form.payment_vendors_id) return channels.filter((channel) => channel.value === 'BNI')
    if (isBcaPaymentVendor(selectedVendor?.enviroment_type)) {
      setactivePickerChannel(false)
      return channels.filter((channel) => channel.value === 'BCA')
    }
    if (isBniPaymentVendor(selectedVendor?.enviroment_type)) {
      setactivePickerChannel(false)
      return channels.filter((channel) => channel.value === 'BNI')
    }
    return channels
  }, [channels, form.payment_vendors_id, selectedVendor])

  if (!show) return null

  const setField = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value
    setForm((current) => ({ ...current, [field]: value }))
  }

  const handleVendorChange = (event) => {
    const paymentVendorsId = event.target.value
    const vendor = snapVendors.find((item) => String(item.id) === paymentVendorsId)
    let bankChannel = form.bank_channel

    if (!paymentVendorsId) {
      setactivePickerChannel(true)
      bankChannel = 'BNI'
    }
    if (isBcaPaymentVendor(vendor?.enviroment_type)) {
      setactivePickerChannel(false)
      bankChannel = 'BCA'
    }
    if (isBniPaymentVendor(vendor?.enviroment_type)) {
      setactivePickerChannel(false)
      bankChannel = 'BNI'
    }
    if (isDokuPaymentVendor(vendor?.enviroment_type)) {
      setactivePickerChannel(true)
      bankChannel = channels[0]?.value || ''
    }

    setForm((current) => ({
      ...current,
      payment_vendors_id: paymentVendorsId,
      bank_channel: bankChannel
    }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    onSubmit({
      ...form,
      name: form.name.trim(),
      patner_service_id: form.patner_service_id.trim(),
      prefix_cust_no: form.prefix_cust_no.trim(),
      bank_channel: form.bank_channel.toUpperCase()
    })
  }

  const merchantBin = `${form.patner_service_id.trim()}${form.prefix_cust_no.trim()}`

  return (
    <>
      <div className="modal-backdrop fade show" onClick={submitting ? undefined : onClose} />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">{isAdd ? 'Add bank route' : 'Edit bank route'}</h5>
                  <small className="text-muted">Route a virtual-account prefix to BNI, BCA SNAP, or DOKU SNAP.</small>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={submitting} />
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-name">Route name</label>
                    <input
                      id="bank-route-name"
                      className="form-control"
                      value={form.name}
                      maxLength={40}
                      onChange={setField('name')}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-vendor">Payment provider</label>
                    <select
                      id="bank-route-vendor"
                      className="form-select"
                      value={form.payment_vendors_id}
                      onChange={handleVendorChange}
                    >
                      <option value="">BNI eCollection (built-in)</option>
                      {missingVendor && (
                        <option value={form.payment_vendors_id}>Unavailable vendor ({form.payment_vendors_id})</option>
                      )}
                      {snapVendors.map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.name} ({vendor.enviroment_type}){vendor.enabled ? '' : ' - disabled'}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Disabled vendors can be saved, but their routes cannot resolve until enabled.</small>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-partner">Partner service ID</label>
                    <input
                      id="bank-route-partner"
                      className="form-control"
                      value={form.patner_service_id}
                      maxLength={30}
                      onChange={setField('patner_service_id')}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-prefix">Customer number prefix</label>
                    <input
                      id="bank-route-prefix"
                      className="form-control"
                      value={form.prefix_cust_no}
                      maxLength={30}
                      onChange={setField('prefix_cust_no')}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-channel">Bank channel ({!activePickerChannel ? 'x' : '+'})</label>
                    <select
                      id="bank-route-channel"
                      className="form-select"
                      disabled={!activePickerChannel}
                      value={form.bank_channel}
                      onChange={setField('bank_channel')}
                      required
                    >
                      {availableChannels.map((channel) => (
                        <option
                          key={channel.value}
                          value={channel.value}>
                          {channel.name} : {channel.value}
                        </option>
                      ))}
                    </select>
                    {availableChannels.length === 0 && (
                      <small className="text-danger">No compatible channels are available.</small>
                    )}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="bank-route-bin">Merchant BIN</label>
                    <input id="bank-route-bin" className="form-control" value={merchantBin} disabled />
                    <small className="text-muted">Generated from partner service ID + customer prefix.</small>
                  </div>

                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        id="bank-route-enabled"
                        className="form-check-input"
                        type="checkbox"
                        checked={form.enabled}
                        onChange={setField('enabled')}
                      />
                      <label className="form-check-label" htmlFor="bank-route-enabled">Enable this route immediately</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-light text-black" onClick={onClose} disabled={submitting}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={submitting || missingVendor || availableChannels.length === 0}
                >
                  <i className={`fa-solid ${submitting ? 'fa-circle-o-notch fa-spin' : 'fa-floppy-disk'}`} />{' '}
                  {submitting ? 'Saving...' : (isAdd ? 'Add route' : 'Save route')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default BankRouteModal
